import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  Avatar,
  Button,
  TextField,
  Divider,
  useTheme,
  Chip,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Snackbar,
  Tabs,
  Tab
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TranslateIcon from '@mui/icons-material/Translate';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PhoneIcon from '@mui/icons-material/Phone';
import WechatIcon from '@mui/icons-material/Chat';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import UndoIcon from '@mui/icons-material/Undo';
import InfoIcon from '@mui/icons-material/Info';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ErrorIcon from '@mui/icons-material/Error';
import {
  getUserInfo,
  sendSmsCode,
  bindPhone,
  verifyCurrentPhone,
  verifyNewPhoneAndComplete,
  generateWechatQRCode,
  getWechatQRCodeStatus,
  bindWechat,
  unbindWechat,
  updateUsername
} from '../store/actions/authActions';
import { getPointsTransactions } from '../store/actions/orderAction';
import CustomerServiceModal from '../components/contact/CustomerServiceModal';
import logger from '../utils/logger';

// 手机号绑定/修改弹窗组件
const PhoneBindModal = ({ open, onClose, currentPhone, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [step, setStep] = useState(1); // 1: 验证当前手机号(修改模式), 2: 输入新手机号, 3: 验证新手机号
  const [mode, setMode] = useState('bind'); // 'bind' 或 'modify'
  const [loading, setLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false); // 独立的验证码按钮loading状态
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentVerificationId, setCurrentVerificationId] = useState(''); // 保存当前手机号验证ID
  
  const [formData, setFormData] = useState({
    currentVerifyCode: '',
    newPhone: '',
    newVerifyCode: ''
  });

  // 根据是否有当前手机号决定模式
  useEffect(() => {
    if (currentPhone) {
      setMode('modify');
      setStep(1); // 修改模式从验证当前手机号开始
    } else {
      setMode('bind');
      setStep(2); // 绑定模式直接输入新手机号
    }
  }, [currentPhone, open]);

  // 重置状态
  const resetModal = () => {
    setFormData({
      currentVerifyCode: '',
      newPhone: '',
      newVerifyCode: ''
    });
    setError('');
    setSuccess('');
    setCountdown(0);
    setLoading(false);
    setSmsLoading(false); // 重置验证码按钮loading状态
    setCurrentVerificationId(''); // 重置验证ID
    if (currentPhone) {
      setMode('modify');
      setStep(1);
    } else {
      setMode('bind');
      setStep(2);
    }
  };

  // 处理关闭
  const handleClose = () => {
    resetModal();
    onClose();
  };

  // 处理输入变化
  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
    setSuccess('');
  };

  // 发送验证码到当前手机号
  const sendCurrentPhoneCode = async () => {
    setSmsLoading(true); // 使用独立的loading状态
    setError('');
    setSuccess('');
    
    try {
      // verify_current类型不需要传递手机号，服务端会从token中获取
      await dispatch(sendSmsCode(null, 'verify_current'));
      
      setSmsLoading(false);
      setCountdown(60);
      setSuccess(`验证码已发送到当前绑定的手机号`);
      
      // 倒计时
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      setSmsLoading(false);
      setError(error.message || '发送验证码失败');
    }
  };

  // 发送验证码到新手机号
  const sendNewPhoneCode = async () => {
    if (!formData.newPhone) {
      setError('请输入手机号');
      return;
    }
    
    if (!/^1[3-9]\d{9}$/.test(formData.newPhone)) {
      setError('请输入正确的手机号');
      return;
    }

    if (formData.newPhone === currentPhone) {
      setError('新手机号不能与当前手机号相同');
      return;
    }

    setSmsLoading(true); // 使用独立的loading状态
    setError('');
    setSuccess('');
    
    try {
      await dispatch(sendSmsCode(formData.newPhone, mode === 'bind' ? 'bind' : 'verify_new'));
      
      setSmsLoading(false);
      setCountdown(60);
      setSuccess(`验证码已发送到 ${formData.newPhone}`);
      
      // 倒计时
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      setSmsLoading(false);
      setError(error.message || '发送验证码失败');
    }
  };

  // 验证当前手机号
  const handleVerifyCurrentPhone = async () => {
    if (!formData.currentVerifyCode) {
      setError('请输入验证码');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await dispatch(verifyCurrentPhone(formData.currentVerifyCode));
      
      // 保存验证ID，用于第二步
      setCurrentVerificationId(response.verificationId);
      
      setLoading(false);
      setSuccess('当前手机号验证成功');
      
      // 重置倒计时状态，确保下一步的按钮状态正确
      setCountdown(0);
      
      setStep(2); // 进入输入新手机号步骤
      
    } catch (error) {
      setLoading(false);
      setError(error.message || '验证失败');
    }
  };

  // 完成绑定或修改
  const handleCompletePhoneBind = async () => {
    if (!formData.newPhone || !formData.newVerifyCode) {
      setError('请输入手机号和验证码');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      let response;
      
      if (mode === 'bind') {
        // 绑定模式
        response = await dispatch(bindPhone(formData.newPhone, formData.newVerifyCode));
      } else {
        // 修改模式
        response = await dispatch(verifyNewPhoneAndComplete(formData.newPhone, formData.newVerifyCode, currentVerificationId));
      }
      
      setLoading(false);
      setSuccess(mode === 'bind' ? '手机号绑定成功！' : '手机号修改成功！');
      
      // 延迟关闭弹窗并调用成功回调
      setTimeout(() => {
        onSuccess && onSuccess(formData.newPhone);
        handleClose();
      }, 1500);
      
    } catch (error) {
      setLoading(false);
      setError(error.message || (mode === 'bind' ? '绑定失败' : '修改失败'));
    }
  };

  // 获取标题
  const getTitle = () => {
    if (mode === 'bind') {
      return '绑定手机号';
    } else {
      if (step === 1) {
        return '验证当前手机号';
      } else {
        return '修改手机号';
      }
    }
  };

  // 获取主按钮文本
  const getMainButtonText = () => {
    if (mode === 'bind') {
      return '绑定手机号';
    } else {
      if (step === 1) {
        return '验证并继续';
      } else {
        return '完成修改';
      }
    }
  };

  // 处理主按钮点击
  const handleMainButtonClick = () => {
    if (mode === 'bind') {
      handleCompletePhoneBind();
    } else {
      if (step === 1) {
        handleVerifyCurrentPhone();
      } else {
        handleCompletePhoneBind();
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: '480px',
          margin: 2
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* 头部 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            pb: 2
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main, flex: 1, textAlign: 'center' }}>
            {getTitle()}
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ position: 'absolute', right: 16, top: 16 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 内容 */}
        <Box sx={{ p: 3, pt: 1 }}>
          {/* 提示信息 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* 修改模式 - 第一步：验证当前手机号 */}
          {mode === 'modify' && step === 1 && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                为了您的账号安全，请先验证当前手机号：{currentPhone}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <TextField
                  flex={1}
                  label="验证码"
                  value={formData.currentVerifyCode}
                  onChange={handleInputChange('currentVerifyCode')}
                  placeholder="请输入验证码"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={sendCurrentPhoneCode}
                  disabled={countdown > 0 || smsLoading}
                  sx={{
                    minWidth: '120px',
                    height: '56px',
                    textTransform: 'none'
                  }}
                >
                  {smsLoading ? (
                    <CircularProgress size={20} />
                  ) : countdown > 0 ? (
                    `${countdown}s`
                  ) : (
                    '发送验证码'
                  )}
                </Button>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleMainButtonClick}
                disabled={loading}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : getMainButtonText()}
              </Button>
            </Box>
          )}

          {/* 第二步：输入新手机号（绑定模式或修改模式的第二步） */}
          {(mode === 'bind' || (mode === 'modify' && step === 2)) && (
            <Box sx={{ py: 2 }}>
              {mode === 'modify' && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                  请输入新的手机号码
                </Typography>
              )}
              
              <TextField
                fullWidth
                label="手机号"
                value={formData.newPhone}
                onChange={handleInputChange('newPhone')}
                placeholder="请输入手机号"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <TextField
                  flex={1}
                  label="验证码"
                  value={formData.newVerifyCode}
                  onChange={handleInputChange('newVerifyCode')}
                  placeholder="请输入验证码"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={sendNewPhoneCode}
                  disabled={countdown > 0 || smsLoading}
                  sx={{
                    minWidth: '120px',
                    height: '56px',
                    textTransform: 'none'
                  }}
                >
                  {smsLoading ? (
                    <CircularProgress size={20} />
                  ) : countdown > 0 ? (
                    `${countdown}s`
                  ) : (
                    '发送验证码'
                  )}
                </Button>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleMainButtonClick}
                disabled={loading}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : getMainButtonText()}
              </Button>
            </Box>
          )}

          {/* 底部提示 */}
          <Divider sx={{ my: 3 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', lineHeight: 1.5 }}
          >
            {mode === 'bind' ? '绑定手机号后可以使用手机号登录' : '修改手机号后将使用新手机号登录'}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// 微信解绑确认弹窗组件
const WechatUnbindModal = ({ open, onClose, onConfirm, loading, wechatNickname }) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: '400px'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
          确认解除微信绑定
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <WechatIcon sx={{ fontSize: 60, color: '#07c160', mb: 2 }} />
        <Typography variant="body1" sx={{ mb: 2 }}>
          您确定要解除与微信账号 <strong>{wechatNickname || '微信用户'}</strong> 的绑定吗？
        </Typography>
        <Typography variant="body2" color="text.secondary">
          解除绑定后，您将无法使用微信登录，但仍可通过其他方式登录
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{ flex: 1, mr: 1, textTransform: 'none' }}
        >
          取消
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          sx={{ flex: 1, ml: 1, textTransform: 'none' }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : '确认解绑'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 微信绑定弹窗组件
const WechatBindModal = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 微信绑定相关状态
  const [wechatQRCode, setWechatQRCode] = useState(null);
  const [wechatStatus, setWechatStatus] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [qrGenerationFailed, setQrGenerationFailed] = useState(false); // 二维码生成最终失败标记
  
  // 使用useRef来跟踪轮询定时器，确保能正确清除
  const wechatPollingRef = React.useRef(null);
  const pollingStartTimeRef = React.useRef(null); // 记录轮询开始时间
  const qrRetryCountRef = React.useRef(0); // 使用ref跟踪重试次数，避免useCallback依赖问题
  const retryTimeoutRef = React.useRef(null); // 跟踪重试定时器
  const qrGenerationFailedRef = React.useRef(false); // 使用ref跟踪失败状态，避免useCallback依赖问题

  // 停止微信状态轮询
  const stopWechatStatusPolling = React.useCallback(() => {
    if (wechatPollingRef.current) {
      //console.log('停止微信绑定轮询定时器:', wechatPollingRef.current);
      clearTimeout(wechatPollingRef.current);
      wechatPollingRef.current = null;
    }
    // 清理轮询开始时间
    pollingStartTimeRef.current = null;
    
    // 清理重试定时器
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // 开始轮询微信扫码状态
  const startWechatStatusPolling = React.useCallback((sceneStr) => {
    // 清除现有的轮询
    if (wechatPollingRef.current) {
      //console.log('清除现有绑定轮询定时器:', wechatPollingRef.current);
      clearTimeout(wechatPollingRef.current);
    }
    
    // 记录轮询开始时间
    pollingStartTimeRef.current = Date.now();
    
    const pollStatus = async () => {
      try {
        const statusData = await dispatch(getWechatQRCodeStatus(sceneStr));
        
        //console.log('微信绑定状态查询结果:', statusData);
        setWechatStatus(statusData.status);
        
        switch (statusData.status) {
          case 'pending':
            // 移除自动显示提示消息，保持简洁
            break;
          case 'scanned':
            setSuccess('已扫码，请在微信中确认绑定');
            break;
          case 'confirmed':
            // 绑定成功
            setSuccess('扫码成功，正在处理绑定...');
            if (wechatPollingRef.current) {
              //console.log('绑定成功，清除轮询定时器:', wechatPollingRef.current);
              clearTimeout(wechatPollingRef.current);
              wechatPollingRef.current = null;
            }
            
            // 处理绑定成功
            try {
              const bindResult = await dispatch(bindWechat(sceneStr));
              //console.log('微信绑定成功:', bindResult);
              setSuccess('微信绑定成功！');
              
              // 延迟关闭弹窗并调用成功回调
              setTimeout(() => {
                onSuccess && onSuccess();
                handleClose();
              }, 1500);
              
            } catch (bindError) {
              //console.error('微信绑定处理失败:', bindError);
              setError(bindError.message || '绑定处理失败');
            }
            break;
          case 'expired':
            setError('二维码已过期，请重新获取');
            if (wechatPollingRef.current) {
              //console.log('二维码过期，清除轮询定时器:', wechatPollingRef.current);
              clearTimeout(wechatPollingRef.current);
              wechatPollingRef.current = null;
            }
            setWechatQRCode(null);
            break;
          case 'not_found':
            setError('二维码不存在或已过期');
            if (wechatPollingRef.current) {
              //console.log('二维码不存在，清除轮询定时器:', wechatPollingRef.current);
              clearTimeout(wechatPollingRef.current);
              wechatPollingRef.current = null;
            }
            setWechatQRCode(null);
            break;
          default:
            break;
        }
      } catch (error) {
        logger.error('查询微信绑定状态失败:', error);
        setError(error.message || '查询状态失败');
        if (wechatPollingRef.current) {
          logger.warn('查询失败，清除轮询定时器:', wechatPollingRef.current);
          clearTimeout(wechatPollingRef.current);
          wechatPollingRef.current = null;
        }
      }
    };
    
    // 动态轮询间隔函数
    const scheduleNextPoll = () => {
      if (!pollingStartTimeRef.current) return;
      
      const elapsed = Date.now() - pollingStartTimeRef.current;
      let interval;
      
      if (elapsed < 30000) {
        // 前30秒：每1.5秒查询一次
        interval = 1500;
      } else if (elapsed < 120000) {
        // 30-120秒：每3秒查询一次
        interval = 3000;
      } else {
        // 120秒后：每5秒查询一次
        interval = 5000;
      }
      
      logger.debug(`设置绑定轮询间隔: ${interval}ms (已运行: ${Math.floor(elapsed/1000)}s)`);
      
      wechatPollingRef.current = setTimeout(() => {
        pollStatus().then(() => {
          // 继续安排下次查询，直到轮询被手动停止
          if (pollingStartTimeRef.current) {
            scheduleNextPoll();
          }
        }).catch((error) => {
          logger.error('轮询查询失败:', error);
          // 即使查询失败，也继续轮询（除非手动停止）
          if (pollingStartTimeRef.current) {
            scheduleNextPoll();
          }
        });
      }, interval);
    };
    
    // 立即执行一次查询
    pollStatus().then(() => {
      // 首次查询完成后开始动态轮询
      if (pollingStartTimeRef.current) {
        logger.debug('首次查询完成，开始动态轮询');
        scheduleNextPoll();
      }
    }).catch((error) => {
      logger.error('首次状态查询失败:', error);
      // 即使首次查询失败，也开始轮询
      if (pollingStartTimeRef.current) {
        logger.debug('首次查询失败，仍开始动态轮询');
        scheduleNextPoll();
      }
    });
    
    logger.debug('启动微信绑定动态轮询，场景:', sceneStr);
  }, [dispatch]);

  // 生成微信绑定二维码（使用useCallback避免无限循环）
  const generateBindQRCode = React.useCallback(async (isRetry = false) => {
    // 清除之前的重试定时器
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    // 防止重复生成
    if (isGenerating) {
      logger.debug('正在生成绑定二维码，跳过重复请求');
      return;
    }
    
    // 如果已经最终失败，不再重试
    if (qrGenerationFailedRef.current && !isRetry) {
      logger.error('绑定二维码生成已最终失败，跳过请求');
      return;
    }
    
    // 检查重试次数限制
    if (isRetry && qrRetryCountRef.current >= 3) {
      logger.error('绑定二维码生成重试次数已达上限(3次)');
      setError('绑定二维码生成失败，请稍后再试或刷新页面');
      setQrGenerationFailed(true); // 标记为最终失败
      qrGenerationFailedRef.current = true; // 同时更新ref
      setLoading(false); // 最终失败时停止loading
      return;
    }
    
    setIsGenerating(true);
    // 只在首次请求时设置loading，重试时保持loading状态
    if (!isRetry) {
      setLoading(true);
    }
    setError('');
    setSuccess('');
    setQrGenerationFailed(false); // 重置失败标记
    qrGenerationFailedRef.current = false; // 同时更新ref
    
    try {
      //console.log(`开始生成微信绑定二维码... ${isRetry ? `(重试第${qrRetryCountRef.current + 1}次)` : ''}`);
      const qrCodeData = await dispatch(generateWechatQRCode('bind'));
      
      setWechatQRCode(qrCodeData);
      setWechatStatus('pending');
      setLoading(false); // 成功时停止loading
      
      // 重置重试计数和失败标记
      qrRetryCountRef.current = 0;
      setQrGenerationFailed(false);
      qrGenerationFailedRef.current = false; // 同时更新ref
      setIsGenerating(false); // 成功时重置
      
      //console.log('微信绑定二维码生成成功:', qrCodeData);
      
      // 开始轮询状态
      startWechatStatusPolling(qrCodeData.scene_str);
      
    } catch (error) {
      // 重试过程中不停止loading，保持loading状态
      //console.error('生成微信绑定二维码失败:', error);
      
      const currentRetryCount = isRetry ? qrRetryCountRef.current + 1 : 1;
      qrRetryCountRef.current = currentRetryCount;
      
      if (currentRetryCount < 3) {
        // 还可以重试，保持loading状态，不重置isGenerating
        logger.debug(`准备重试生成绑定二维码 (${currentRetryCount}/3)`);
        // 重试过程中不显示错误信息，保持loading状态
        
        // 延迟1秒后重试
        retryTimeoutRef.current = setTimeout(() => {
          generateBindQRCode(true);
        }, 1000);
      } else {
        // 重试次数已达上限，停止loading并显示错误
        logger.error('绑定二维码生成重试次数已达上限');
        //setError('网络连接失败，请稍后再试');
        setQrGenerationFailed(true); // 标记为最终失败
        qrGenerationFailedRef.current = true; // 同时更新ref
        setIsGenerating(false); // 最终失败时重置
        setLoading(false); // 最终失败时停止loading
      }
    }
  }, [dispatch, startWechatStatusPolling, isGenerating]); // 移除qrGenerationFailed依赖

  // 刷新微信二维码
  const refreshWechatQRCode = () => {
    stopWechatStatusPolling();
    setWechatQRCode(null);
    setWechatStatus('');
    setIsGenerating(false); // 重置生成状态
    qrRetryCountRef.current = 0; // 重置重试计数
    setQrGenerationFailed(false); // 重置失败标记
    qrGenerationFailedRef.current = false; // 同时更新ref
    generateBindQRCode();
  };

  // 处理关闭
  const handleClose = () => {
    stopWechatStatusPolling();
    setWechatQRCode(null);
    setWechatStatus('');
    setIsGenerating(false);
    qrRetryCountRef.current = 0; // 重置重试计数
    setQrGenerationFailed(false); // 重置失败标记
    qrGenerationFailedRef.current = false; // 同时更新ref
    setError('');
    setSuccess('');
    setLoading(false);
    onClose();
  };

  // 当弹窗打开时生成二维码
  React.useEffect(() => {
    // 当登录弹窗打开且没有二维码且没有正在生成且没有最终失败时，生成二维码
    if (open && !wechatQRCode && !isGenerating && !qrGenerationFailed) {
      //console.log('微信绑定弹窗打开，生成二维码');
      generateBindQRCode();
    }
  }, [open, wechatQRCode, isGenerating, qrGenerationFailed]); // 添加qrGenerationFailed依赖

  // 组件卸载时清理轮询
  React.useEffect(() => {
    return () => {
      stopWechatStatusPolling();
    };
  }, [stopWechatStatusPolling]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: '480px',
          margin: 2,
          height: '500px', // 从600px调整为500px
          maxHeight: '500px' // 确保不超过固定高度
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 2, position: 'relative' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          绑定微信账号
        </Typography>
        <IconButton 
          onClick={handleClose} 
          size="small" 
          sx={{ position: 'absolute', right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        textAlign: 'center', 
        py: 3,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: 1, // 占满剩余空间
        minHeight: 0 // 允许内容收缩
      }}>
        {/* 提示信息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* 主要内容区域 - 使用固定高度容器 */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '320px', // 从400px调整为320px
          maxHeight: '320px'  // 从400px调整为320px
        }}>
          {loading && !wechatQRCode ? (
            // 正在生成二维码（包括重试过程）
            <Box sx={{ py: 4 }}>
              <CircularProgress size={60} sx={{ color: theme.palette.primary.main, mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                正在生成绑定二维码...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                
              </Typography>
            </Box>
          ) : wechatQRCode ? (
            // 显示二维码
            <Box>
              <Box
                sx={{
                  width: 200,
                  height: 200,
                  mx: 'auto',
                  mb: 2,
                  border: '1px solid #eee',
                  borderRadius: 2,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff'
                }}
              >
                <img 
                  src={wechatQRCode.qr_url} 
                  alt="微信绑定二维码" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain' 
                  }} 
                />
              </Box>
              
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {wechatStatus === 'scanned' ? '请在微信中确认绑定' : '扫码绑定微信'}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {wechatStatus === 'scanned' 
                  ? '已扫码，请在微信中点击确认绑定' 
                  : '使用微信扫描二维码即可绑定'
                }
              </Typography>
            </Box>
          ) : qrGenerationFailed ? (
            // 二维码生成失败时显示错误信息
            <Box sx={{ py: 4 }}>
              
              {/* 错误图标 */}
              <Box sx={{ mb: 2 }}>
                <ErrorIcon 
                  sx={{ 
                    fontSize: '60px', 
                    color: theme.palette.error.main, 
                    mb: 0,
                    fontWeight: 300
                  }}
                />
              </Box>
              
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'error.main' }}>
                请求失败
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                请稍后再试或使用其它方式
              </Typography>
            
            </Box>
          ) : (
            // 初始状态：没有二维码时显示的内容
            <Box sx={{ py: 4 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                
              </Typography>
             
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const Profile = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loading } = useSelector((state) => state.auth);
  const { pointsTransactions } = useSelector((state) => state.order);
  
  // 独立编辑状态
  const [editingStates, setEditingStates] = useState({
    username: false,
    phone: false,
    wechat: false
  });
  
  const [userInfoLoading, setUserInfoLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 手机号绑定弹窗状态
  const [phoneBindModalOpen, setPhoneBindModalOpen] = useState(false);
  
  // 微信相关弹窗状态
  const [wechatUnbindModalOpen, setWechatUnbindModalOpen] = useState(false);
  const [wechatBindModalOpen, setWechatBindModalOpen] = useState(false);
  const [wechatActionLoading, setWechatActionLoading] = useState(false);
  
  // 客服弹窗状态
  const [customerServiceModalOpen, setCustomerServiceModalOpen] = useState(false);
  
  // Toast提示状态
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });
  
  // 临时编辑值
  const [tempValues, setTempValues] = useState({
    username: '',
    phone: '',
    wechat: ''
  });

  // 交易记录筛选状态
  const [transactionFilter, setTransactionFilter] = useState('all'); // 'all', 'recharge', 'consume', 'refund'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // 每页显示10条记录

  // 组件加载时获取用户信息
  useEffect(() => {
    const fetchUserInfo = async () => {
      setUserInfoLoading(true);
      setError('');
      try {
        await dispatch(getUserInfo());
      } catch (error) {
        logger.error('获取用户信息失败:', error);
        setError('获取用户信息失败，请刷新页面重试');
      } finally {
        setUserInfoLoading(false);
      }
    };

    fetchUserInfo();
  }, [dispatch]);

  // 获取积分交易记录
  const fetchPointsTransactions = async (page = 1, type = 'all') => {
    try {
      const params = { page, limit: itemsPerPage };
      if (type !== 'all') params.type = type;
      await dispatch(getPointsTransactions(params));
      
      } catch (error) {
        logger.error('获取积分交易记录失败:', error);
      showToast(error.message || '获取积分交易记录失败', 'error');
      }
    };

  // 初始加载交易记录
  useEffect(() => {
    if (user) {
      fetchPointsTransactions(1, transactionFilter);
    }
  }, [user, transactionFilter]);

  // 更新临时编辑值
  useEffect(() => {
    if (user) {
      setTempValues({
        username: user.username || '',
        phone: user.phone || '',
        wechat: user.wechatNickname || ''
      });
    }
  }, [user]);

  // 处理页码变化
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchPointsTransactions(value, transactionFilter);
  };

  // 处理交易类型筛选变化
  const handleTransactionFilterChange = (event, newValue) => {
    setTransactionFilter(newValue);
    setCurrentPage(1);
    fetchPointsTransactions(1, newValue);
  };

  // 获取交易类型的显示文本和颜色
  const getTransactionTypeInfo = (type) => {
    switch (type) {
      case 'recharge':
        return {
          text: '充值',
          color: theme.palette.success.main,
          bgColor: 'rgba(76, 175, 80, 0.1)',
          icon: <AddIcon sx={{ fontSize: 16 }} />
        };
      case 'consume':
        return {
          text: '消费',
          color: theme.palette.error.main,
          bgColor: 'rgba(244, 67, 54, 0.1)',
          icon: <RemoveIcon sx={{ fontSize: 16 }} />
        };
      case 'refund':
        return {
          text: '退款',
          color: theme.palette.warning.main,
          bgColor: 'rgba(255, 193, 7, 0.1)',
          icon: <UndoIcon sx={{ fontSize: 16 }} />
        };
      case 'invite_reward':
        return {
          text: '邀请奖励',
          color: theme.palette.info.main,
          bgColor: 'rgba(33, 150, 243, 0.1)',
          icon: <AddIcon sx={{ fontSize: 16 }} />
        };
      default:
        return {
          text: type,
          color: theme.palette.text.primary,
          bgColor: 'rgba(0, 0, 0, 0.05)',
          icon: <InfoIcon sx={{ fontSize: 16 }} />
        };
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 解码文件名
  const decodeFileName = (fileName) => {
    try {
      return decodeURIComponent(fileName);
    } catch (e) {
      return fileName;
    }
  };

  // 解码交易描述中的文件名
  const decodeTransactionDescription = (description) => {
    try {
      // 匹配描述中的文件名模式，如 "创建翻译任务: 文件名"
      const fileNameMatch = description.match(/^(.*?:\s*)(.+)$/);
      if (fileNameMatch) {
        const prefix = fileNameMatch[1]; // "创建翻译任务: "
        const fileName = fileNameMatch[2]; // 文件名部分
        
        // 检查文件名是否包含URL编码字符
        if (fileName.includes('%')) {
          return prefix + decodeURIComponent(fileName);
        }
      }
      return description;
    } catch (e) {
      return description;
    }
  };

  // 开始编辑某个字段
  const handleStartEdit = (field) => {
    setEditingStates(prev => ({
      ...prev,
      [field]: true
    }));
    // 设置当前值到临时编辑值
    if (field === 'username') {
      setTempValues(prev => ({ ...prev, username: user.username || '' }));
    }
  };

  // 保存编辑
  const handleSaveEdit = async (field) => {
    if (field === 'username') {
      // 验证用户名
      const newUsername = tempValues.username.trim();
      
      if (!newUsername) {
        showToast('用户名不能为空', 'error');
        return;
      }
      
      if (newUsername.length < 2 || newUsername.length > 20) {
        showToast('用户名长度必须在2-20个字符之间', 'error');
        return;
      }
      
      // 检查用户名格式（只允许中文、英文、数字、下划线）
      const usernameRegex = /^[\u4e00-\u9fa5a-zA-Z0-9_]+$/;
      if (!usernameRegex.test(newUsername)) {
        showToast('用户名只能包含中文、英文、数字和下划线', 'error');
        return;
      }
      
      // 检查是否有变化
      if (newUsername === user.username) {
        setEditingStates(prev => ({
          ...prev,
          [field]: false
        }));
        return;
      }
      
      try {
        // 设置加载状态
        setUserInfoLoading(true);
        
        // 调用修改用户名API
        const result = await dispatch(updateUsername(newUsername));
        
        // 修改成功
        setEditingStates(prev => ({
          ...prev,
          [field]: false
        }));
        
        showToast(result.message || '用户名修改成功！', 'success');
        
      } catch (error) {
        logger.error('修改用户名失败:', error);
        showToast(error.message || '修改用户名失败，请重试', 'error');
      } finally {
        setUserInfoLoading(false);
      }
    } else {
      // 其他字段的保存逻辑
      try {
        logger.debug(`保存${field}:`, tempValues[field]);
        
        // TODO: 这里可以添加其他字段的保存逻辑
        
        setEditingStates(prev => ({
          ...prev,
          [field]: false
        }));
      } catch (error) {
        logger.error(`保存${field}失败:`, error);
        showToast(`保存${field}失败，请重试`, 'error');
      }
    }
  };

  // 取消编辑
  const handleCancelEdit = (field) => {
    setEditingStates(prev => ({
      ...prev,
      [field]: false
    }));
    // 恢复原值
    if (field === 'username') {
      setTempValues(prev => ({ ...prev, username: user.username || '' }));
    }
  };

  // 处理输入值变化
  const handleTempValueChange = (field, value) => {
    setTempValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 处理手机号绑定/修改
  const handlePhoneAction = () => {
    setPhoneBindModalOpen(true);
  };

  // 处理手机号绑定成功
  const handlePhoneBindSuccess = async (newPhone) => {
    logger.info('手机号绑定/修改成功:', newPhone);
    // 重新获取用户信息以更新显示
    try {
      await dispatch(getUserInfo());
    } catch (error) {
      logger.error('更新用户信息失败:', error);
    }
  };

  // 显示Toast提示
  const showToast = (message, severity = 'success') => {
    setToast({
      open: true,
      message,
      severity
    });
  };

  // 关闭Toast
  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast(prev => ({ ...prev, open: false }));
  };

  // 处理微信解绑确认
  const handleWechatUnbindConfirm = async () => {
    setWechatActionLoading(true);
    
    try {
      const result = await dispatch(unbindWechat());
      //console.log('微信解绑成功:', result);
      
      setWechatUnbindModalOpen(false);
      setWechatActionLoading(false);
      
      // 显示成功提示
      showToast(result.message || '微信解绑成功！', 'success');
      
    } catch (error) {
      //console.error('微信解绑失败:', error);
      setWechatActionLoading(false);
      
      // 显示错误提示
      showToast(error.message || '微信解绑失败', 'error');
    }
  };

  // 处理微信绑定成功
  const handleWechatBindSuccess = () => {
    //console.log('微信绑定成功');
    showToast('微信绑定成功！', 'success');
  };

  // 处理微信绑定/解绑
  const handleWechatAction = () => {
    // 检查用户的微信绑定状态
    const isWechatBound = user?.wechatOpenid || user?.wechatNickname;
    
    if (isWechatBound) {
      // 已绑定，执行解绑操作
      setWechatUnbindModalOpen(true);
    } else {
      // 未绑定，执行绑定操作
      setWechatBindModalOpen(true);
    }
  };

  // 获取用户类型显示文本
  const getUserTypeText = (userType) => {
    switch (userType) {
      case 'user': return '个人用户';
      case 'admin': return '管理员';
      case 'enterprise': return '企业用户';
      case 'agency': return '代理商';
      default: return '个人用户';
    }
  };

  // 处理充值点数跳转
  const handleRecharge = () => {
    navigate('/pricing');
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 判断是否为核心/重要用户
  const isVipUser = () => {
    return user?.userLevel === '核心' || user?.userLevel === '重要';
  };

  // 处理客服弹窗
  const handleCustomerService = () => {
    setCustomerServiceModalOpen(true);
  };

  // 如果正在加载用户信息，显示加载状态
  if (userInfoLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // 如果没有用户数据，显示错误信息
  if (!user) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || '无法获取用户信息，请刷新页面重试'}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          刷新页面
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      {/* 页面标题 */}
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
          fontWeight: 600,
          mb: 4,
          color: theme.palette.primary.main
        }}
      >
        个人中心
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* 左侧：个人信息 */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Avatar
                src={user.wechatAvatar}
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '2rem'
                }}
              >
                {!user.wechatAvatar && (user.username?.charAt(0)?.toUpperCase() || 'U')}
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {user.username || '用户'}
              </Typography>
              <Chip 
                label={getUserTypeText(user.userType)}
                color="primary"
                sx={{ mt: 1 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 个人信息表单 */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  基本信息
                </Typography>

              {/* 用户名信息项 */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 2,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}>
                <Box sx={{ flex: 1 }}>
                  {editingStates.username ? (
                    <TextField
                      fullWidth
                    size="small"
                      value={tempValues.username}
                      onChange={(e) => handleTempValueChange('username', e.target.value)}
                      placeholder="请输入用户名"
                      disabled={userInfoLoading}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          }
                        }
                      }}
                    />
                ) : (
                  <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        用户名
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.username || '未设置'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ ml: 2 }}>
                  {editingStates.username ? (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                      size="small"
                        onClick={() => handleSaveEdit('username')}
                        color="primary"
                        disabled={userInfoLoading}
                      >
                        {userInfoLoading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CheckIcon fontSize="small" />
                        )}
                      </IconButton>
                      <IconButton 
                      size="small"
                        onClick={() => handleCancelEdit('username')}
                      color="secondary"
                        disabled={userInfoLoading}
                    >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                  </Box>
                  ) : (
                    <IconButton 
                      size="small" 
                      onClick={() => handleStartEdit('username')}
                      color="primary"
                      disabled={userInfoLoading}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* 手机号信息项 */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 2,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ color: 'action.active', mr: 1, fontSize: '1.2rem' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      手机号
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {user.phone || '未绑定'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ ml: 2 }}>
                  <IconButton 
                  size="small"
                    onClick={handlePhoneAction}
                    color={user.phone ? "primary" : "success"}
                  >
                    {user.phone ? <EditIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
                  </IconButton>
                </Box>
                </Box>

              {/* 微信信息项 */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 2,
                      '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <WechatIcon sx={{ color: '#07c160', mr: 1, fontSize: '1.2rem' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      微信
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {(user.wechatOpenid || user.wechatNickname) ? (user.wechatNickname || '已绑定微信') : '未绑定'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ ml: 2 }}>
                  <IconButton 
                    size="small" 
                    onClick={handleWechatAction}
                    color={(user.wechatOpenid || user.wechatNickname) ? "error" : "success"}
                  >
                    {(user.wechatOpenid || user.wechatNickname) ? <LinkOffIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Card>

          {/* 点数信息 */}
          <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              点数余额
            </Typography>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {user.points || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                剩余点数
              </Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={handleRecharge}
              sx={{ mt: 2 }}
            >
              充值点数
            </Button>
          </Card>

          {/* 专属客服卡片 - 仅对核心/重要用户显示 */}
          {isVipUser() && (
            <Card sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                专属客服
              </Typography>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <SupportAgentIcon sx={{ fontSize: 50, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  VIP用户专享一对一客服支持
                </Typography>
              </Box>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCustomerService}
                startIcon={<SupportAgentIcon />}
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: theme.palette.primary.main + '10'
                  }
                }}
              >
                联系专属客服
              </Button>
            </Card>
          )}
        </Grid>

        {/* 右侧：统计信息和活动记录 */}
        <Grid item xs={12} md={8}>
          {/* 统计卡片 */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <TranslateIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {user.taskStats?.totalTasks || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  总任务数
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <StarIcon sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  {user.taskStats?.completedTasks || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  已完成
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                  {user.taskStats?.totalPages || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  翻译页数
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                  {user.points || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  剩余点数
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* 点数交易记录 */}
          <Card sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                点数交易记录
            </Typography>
            </Box>

            {/* 交易类型筛选标签 */}
            <Box sx={{ mb: 3 }}>
              <Tabs
                value={transactionFilter}
                onChange={handleTransactionFilterChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  }
                }}
              >
                <Tab
                  value="all"
                  icon={<AllInclusiveIcon />}
                  iconPosition="start"
                  label="全部"
                  sx={{ textTransform: 'none', minHeight: 48 }}
                />
                <Tab
                  value="recharge"
                  icon={<AddIcon />}
                  iconPosition="start"
                  label="充值"
                  sx={{ textTransform: 'none', minHeight: 48, color: theme.palette.success.main }}
                />
                <Tab
                  value="consume"
                  icon={<RemoveIcon />}
                  iconPosition="start"
                  label="消费"
                  sx={{ textTransform: 'none', minHeight: 48, color: theme.palette.error.main }}
                />
                <Tab
                  value="refund"
                  icon={<UndoIcon />}
                  iconPosition="start"
                  label="退款"
                  sx={{ textTransform: 'none', minHeight: 48, color: theme.palette.warning.main }}
                />
              </Tabs>
            </Box>

            {/* 错误状态显示 */}
            {pointsTransactions.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {pointsTransactions.error}
              </Alert>
            )}
            
            {pointsTransactions.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : pointsTransactions.data.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  暂无交易记录
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {transactionFilter === 'all' ? '还没有任何交易记录' : `还没有${getTransactionTypeInfo(transactionFilter).text}记录`}
                </Typography>
              </Box>
            ) : (
              <>
                <Stack spacing={2} sx={{ width: '100%' }}>
                  {pointsTransactions.data.map((transaction) => {
                    const typeInfo = getTransactionTypeInfo(transaction.type);
                    const isPositive = transaction.points > 0;

                    return (
                      <Card 
                        key={transaction._id}
                        sx={{ 
                          p: 3,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
                          },
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {/* 左侧图标 */}
                        <Box
                          sx={{ 
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: typeInfo.bgColor,
                              color: typeInfo.color,
                              flexShrink: 0
                            }}
                          >
                            {typeInfo.icon}
                        </Box>
                          
                          {/* 主要内容 */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* 第一行：交易类型和点数变化 */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={typeInfo.text}
                                  size="small"
                                  sx={{
                                    bgcolor: typeInfo.bgColor,
                                    color: typeInfo.color,
                                    fontWeight: 600,
                                    '& .MuiChip-label': { px: 1.5 }
                                  }}
                                />
                              </Box>
                              
                              {/* 点数变化 */}
                        <Typography 
                                variant="h6"
                          sx={{ 
                                  fontWeight: 700,
                                  color: isPositive ? theme.palette.success.main : theme.palette.error.main
                                }}
                              >
                                {isPositive ? '+' : ''}{transaction.points}
                        </Typography>
                            </Box>

                            {/* 第二行：描述信息 */}
                            <Typography variant="body2" color="text.primary" sx={{ mb: 2, lineHeight: 1.5 }}>
                              {decodeTransactionDescription(transaction.description)}
                            </Typography>

                            {/* 第三行：详细信息标签 */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                              {/* 任务信息 */}
                              {transaction.task_id && (
                                <>
                          <Chip
                                    label={`任务: ${transaction.task_id.task_id}`}
                            size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                  <Chip
                                    label={decodeFileName(transaction.task_id.file_name)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', maxWidth: 200 }}
                                  />
                                </>
                              )}

                              {/* 充值信息 */}
                              {transaction.payment_info && (
                                <>
                            <Chip
                                    label={`订单: ${transaction.payment_info.order_id}`}
                              size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                  <Chip
                                    label={transaction.payment_info.payment_method_text}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                  <Chip
                                    label={`¥${transaction.payment_info.payment_amount}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                  {transaction.payment_info.sku_info && (
                                    <Chip
                                      label={transaction.payment_info.sku_info.sku_name}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                </>
                              )}

                              {/* 余额信息 */}
                          <Chip
                                label={`余额: ${transaction.balance}`}
                            size="small"
                            variant="filled"
                            sx={{ 
                                  fontSize: '0.7rem',
                                  bgcolor: 'rgba(0,0,0,0.06)',
                                  color: 'text.secondary'
                                }}
                              />
                            </Box>

                            {/* 第四行：时间信息 */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(transaction.created_at)}
                          </Typography>
                            
                              {/* 支付时间（充值记录） */}
                              {transaction.payment_info?.paid_at && (
                                <Typography variant="caption" color="text.secondary">
                                  支付时间: {formatDate(transaction.payment_info.paid_at)}
                            </Typography>
                              )}
                          </Box>
                          </Box>
                        </Box>
                      </Card>
                    );
                  })}
                </Stack>

                {/* 分页组件 */}
                {pointsTransactions.pagination.total_pages > 1 && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 4, 
                    mb: 1
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      显示第 {((pointsTransactions.pagination.current_page - 1) * pointsTransactions.pagination.page_size) + 1}-{Math.min(pointsTransactions.pagination.current_page * pointsTransactions.pagination.page_size, pointsTransactions.pagination.total_records)} 项，
                      共 {pointsTransactions.pagination.total_records} 项
                    </Typography>
                    
                    <Pagination
                      count={pointsTransactions.pagination.total_pages}
                      page={pointsTransactions.pagination.current_page}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                      disabled={pointsTransactions.loading}
                    />
                  </Box>
                )}
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* 手机号绑定弹窗 */}
      <PhoneBindModal
        open={phoneBindModalOpen}
        onClose={() => setPhoneBindModalOpen(false)}
        currentPhone={user.phone}
        onSuccess={handlePhoneBindSuccess}
      />

      {/* 微信解绑确认弹窗 */}
      <WechatUnbindModal
        open={wechatUnbindModalOpen}
        onClose={() => setWechatUnbindModalOpen(false)}
        onConfirm={handleWechatUnbindConfirm}
        loading={wechatActionLoading}
        wechatNickname={user.wechatNickname}
      />

      {/* 微信绑定弹窗 */}
      <WechatBindModal
        open={wechatBindModalOpen}
        onClose={() => setWechatBindModalOpen(false)}
        onSuccess={handleWechatBindSuccess}
      />

      {/* 客服弹窗 */}
      <CustomerServiceModal
        open={customerServiceModalOpen}
        onClose={() => setCustomerServiceModalOpen(false)}
      />

      {/* Toast提示 */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile; 